import React, { useEffect, useMemo, useRef, useState } from "react";
import apiClient from "../api";
import SeatLayout from "../components/SeatLayout";

const getLKDate = (offsetDays = 0) => {
  const now = new Date();
  const lk = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Colombo" }));
  lk.setDate(lk.getDate() + offsetDays);

  const y = lk.getFullYear();
  const m = String(lk.getMonth() + 1).padStart(2, "0");
  const d = String(lk.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const todayLK = () => getLKDate(0);

const formatDisplayDate = (value = "") => {
  if (!value) return "Select date";
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;

  return new Date(year, month - 1, day).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export default function OperatorSeatPublicView() {
  const token = new URLSearchParams(window.location.search).get("token");
  const dateInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [seatLoading, setSeatLoading] = useState(false);
  const [error, setError] = useState("");
  const [buses, setBuses] = useState([]);
  const [selectedBusId, setSelectedBusId] = useState("");
  const [date, setDate] = useState(todayLK());
  const [seatData, setSeatData] = useState(null);

  const selectedBus = useMemo(
    () => buses.find((bus) => String(bus._id) === String(selectedBusId)),
    [buses, selectedBusId]
  );

  const openDatePicker = () => {
    const input = dateInputRef.current;

    if (!input) return;

    if (typeof input.showPicker === "function") {
      input.showPicker();
      return;
    }

    input.focus();
    input.click();
  };

  useEffect(() => {
    const verifyToken = async () => {
      try {
        setLoading(true);
        setError("");

        if (!token) {
          setError("Invalid seat-view link.");
          return;
        }

        const res = await apiClient.get("/operator-seat-view/verify", {
          params: { token },
        });

        const list = Array.isArray(res.data?.buses) ? res.data.buses : [];
        setBuses(list);

        if (list.length > 0) {
          setSelectedBusId(String(list[0]._id));
        } else {
          setError("No buses found for this link.");
        }
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "This link is invalid or expired. Please request a new link from WhatsApp."
        );
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  useEffect(() => {
    const loadSeats = async () => {
      try {
        if (!token || !selectedBus || !date) return;

        setSeatLoading(true);
        setError("");

        const res = await apiClient.get("/operator-seat-view/seats", {
          params: {
            token,
            busId: selectedBus._id,
            date,
            departureTime: selectedBus.departureTime,
          },
        });

        setSeatData(res.data);
      } catch (err) {
        setSeatData(null);
        setError(
          err?.response?.data?.message ||
            "Unable to load seat status. Please try again."
        );
      } finally {
        setSeatLoading(false);
      }
    };

    loadSeats();
  }, [token, selectedBus, date]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-700">Loading seat view...</p>
        </div>
      </div>
    );
  }

  if (error && !selectedBus) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-sm border border-red-100 p-6 max-w-md text-center">
          <h1 className="text-lg font-bold text-slate-900 mb-2">Seat View Unavailable</h1>
          <p className="text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 px-3 pt-4 pb-28 sm:px-6 sm:py-8">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className="text-[11px] sm:text-xs font-bold uppercase tracking-[0.22em] text-blue-600">
                Routesbook Operator
              </p>
              <h1 className="text-2xl sm:text-2xl font-extrabold text-slate-900 leading-tight">
                Live Seat View
              </h1>
              <p className="text-sm sm:text-sm text-slate-500 mt-2 leading-6">
                Read-only secure link. Passenger private details are hidden.
              </p>
            </div>

            <div className="rounded-2xl bg-blue-50 border border-blue-100 px-4 py-3">
              <p className="text-xs text-blue-700 font-semibold">Link validity</p>
              <p className="text-sm font-bold text-blue-900">Temporary access</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-4 sm:p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="min-w-0">
              <label className="text-xs font-bold text-slate-500 uppercase">
                Select Bus
              </label>
              <select
                value={selectedBusId}
                onChange={(e) => setSelectedBusId(e.target.value)}
                className="mt-2 w-full min-w-0 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 truncate"
              >
                {buses.map((bus) => (
                  <option key={bus._id} value={bus._id}>
                    {bus.label || `${bus.name} | ${bus.from} → ${bus.to}`}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs font-bold text-slate-500 uppercase">
                Select Date
              </label>

              <button
                type="button"
                onClick={openDatePicker}
                className="relative mt-2 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left outline-none focus:ring-2 focus:ring-blue-500"
              >
                <span className="block text-sm font-extrabold text-slate-900">
                  {formatDisplayDate(date)}
                </span>
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500">
                  📅
                </span>

                <input
                  ref={dateInputRef}
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="absolute inset-0 h-full w-full opacity-0 cursor-pointer"
                  aria-label="Select Date"
                />
              </button>

              <div className="grid grid-cols-3 gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setDate(getLKDate(-1))}
                  className={`px-3 py-2 rounded-full border text-xs font-bold ${
                    date === getLKDate(-1)
                      ? "border-blue-100 text-blue-700 bg-blue-50"
                      : "border-slate-200 text-slate-600 bg-white"
                  }`}
                >
                  Yesterday
                </button>
                <button
                  type="button"
                  onClick={() => setDate(getLKDate(0))}
                  className={`px-3 py-2 rounded-full border text-xs font-bold ${
                    date === getLKDate(0)
                      ? "border-blue-100 text-blue-700 bg-blue-50"
                      : "border-slate-200 text-slate-600 bg-white"
                  }`}
                >
                  Today
                </button>
                <button
                  type="button"
                  onClick={() => setDate(getLKDate(1))}
                  className={`px-3 py-2 rounded-full border text-xs font-bold ${
                    date === getLKDate(1)
                      ? "border-blue-100 text-blue-700 bg-blue-50"
                      : "border-slate-200 text-slate-600 bg-white"
                  }`}
                >
                  Tomorrow
                </button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-100 rounded-2xl p-4 text-sm font-semibold text-red-700">
            {error}
          </div>
        )}

        {seatData && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 font-bold">Total</p>
                <p className="text-3xl font-extrabold text-slate-900">
                  {seatData.totalSeats}
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 font-bold">Available</p>
                <p className="text-3xl font-extrabold text-emerald-600">
                  {seatData.availableSeats}
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 font-bold">Booked/Blocked</p>
                <p className="text-3xl font-extrabold text-red-600">
                  {seatData.bookedSeats?.length || 0}
                </p>
              </div>
              <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <p className="text-xs text-slate-500 font-bold">Window Available</p>
                <p className="text-3xl font-extrabold text-blue-600">
                  {seatData.availableWindowSeats}
                </p>
              </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-3 sm:p-5 overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-extrabold text-slate-900 break-words">
                    {seatData.bus?.label || selectedBus?.label}
                  </h2>
                  <p className="text-sm text-slate-500">
                    {formatDisplayDate(date)} • {selectedBus?.departureTime}
                  </p>
                </div>

                {seatLoading && (
                  <span className="text-xs font-bold text-blue-600">
                    Updating...
                  </span>
                )}
              </div>

              <SeatLayout
                seatLayout={seatData.bus?.seatLayout || selectedBus?.seatLayout || []}
                bookedSeats={seatData.bookedSeats || []}
                selectedSeats={[]}
                bookedSeatGenders={seatData.seatGenderMap || {}}
                onSeatClick={() => {}}
              />

              <div className="flex flex-wrap gap-2 mt-4 text-xs font-semibold text-slate-600">
                <span className="px-3 py-1.5 rounded-full bg-white border border-slate-200">
                  White = Available
                </span>
                <span className="px-3 py-1.5 rounded-full bg-red-50 border border-red-100 text-red-700">
                  Red = Locked / Manual / Hidden gender
                </span>
                <span className="px-3 py-1.5 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700">
                  Icon = Paid booking
                </span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
